import { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { User, Mail, Lock, AlertCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import PhoneInput, { maxDigitsFor } from '../components/PhoneInput';
import { DEFAULT_COUNTRY, type Country } from '../data/countryCodes';

type LoginMethod = 'email' | 'phone';

export default function LoginPage() {
  const { user, login } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/';

  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [form, setForm] = useState({ email: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // If already logged in, redirect
  if (user) return <Navigate to={from} replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const cleanPhone = form.phone ? form.phone.replace(/\D/g, '').slice(0, maxDigitsFor(country)) : '';
    if (loginMethod === 'phone') {
      if (country.iso === 'in') {
        if (cleanPhone.length !== 10) {
          setError('Please enter a valid 10-digit phone number');
          return;
        }
      } else if (!/^\+?\d{7,15}$/.test(`${country.dialCode}${cleanPhone}`)) {
        // Others: mirror the server rule (full number, incl. country code, is 7–15 digits).
        setError('Please enter a valid phone number');
        return;
      }
    }
    setLoading(true);
    try {
      const credentials = loginMethod === 'email'
        ? { email: form.email, password: form.password }
        : { phone: `${country.dialCode}${cleanPhone}`, password: form.password };
      await login(credentials);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inp = `w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.login} />
      <div className={`w-full max-w-md rounded-2xl shadow-xl p-8 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}>

        <Link to="/" className={`inline-flex items-center gap-1 text-xs mb-6 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
          <ArrowLeft size={14} /> Back to Store
        </Link>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
            <User className="text-white" size={24} />
          </div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Welcome Back</h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sign in to your ZeroSmoke account</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Login method toggle */}
        <div className="flex rounded-xl overflow-hidden border mb-5 text-sm">
          {(['email', 'phone'] as LoginMethod[]).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setLoginMethod(m)}
              className={`flex-1 py-2.5 font-medium transition-colors ${
                loginMethod === m
                  ? 'bg-emerald-500 text-white'
                  : isDark ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-gray-50 text-gray-500 border-gray-200'
              }`}
            >
              {m === 'email' ? 'Email' : 'Phone'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {loginMethod === 'email' && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
              <div className="relative">
                <Mail size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={inp} required />
              </div>
            </div>
          )}

          {loginMethod === 'phone' && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Phone Number</label>
              <PhoneInput
                country={country}
                onCountryChange={setCountry}
                value={form.phone}
                onChange={phone => setForm({ ...form, phone })}
                isDark={isDark}
                required
              />
            </div>
          )}

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Password</label>
            <div className="relative">
              <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className={inp}
                required
                minLength={6}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors">
            {loading ? 'Please wait...' : 'Sign In'}
          </button>

          <div className="text-right mt-2">
            <Link to="/forgot-password" className={`text-xs ${isDark ? 'text-gray-500 hover:text-emerald-400' : 'text-gray-400 hover:text-emerald-600'}`}>
              Forgot password?
            </Link>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Don't have an account?{' '}
            <Link to="/signup" className="text-emerald-500 font-medium hover:underline">Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
